import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import DeleteIcon from "@mui/icons-material/Delete";
import Popup from "../../components/Popup";

const getBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    return baseUrl.replace(/\/+$/, "");
};

const initialForm = {
    name: "",
    email: "",
    password: "",
    schoolName: "",
    photo: ""
};

const SuperAdminBoardPage = () => {
    const [login, setLogin] = useState({ email: "", password: "" });
    const [superAdmin, setSuperAdmin] = useState(null);
    const [loading, setLoading] = useState(false);
    const [admins, setAdmins] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    // Finance Code state
    const [financeCodes, setFinanceCodes] = useState({});
    const [financeCodeInputs, setFinanceCodeInputs] = useState({});
    const [financeCodeLoading, setFinanceCodeLoading] = useState({});

    const boardTitle = useMemo(
        () => (editingId ? "Update Admin" : "Create Admin"),
        [editingId]
    );

    const handleLogout = () => {
        setSuperAdmin(null);
        setLogin({ email: "", password: "" });
        setAdmins([]);
        setForm(initialForm);
        setEditingId(null);
    };

    const showMessage = (text) => {
        setMessage(text);
        setShowPopup(true);
    };

    const fetchAdmins = async () => {
        try {
            const result = await axios.get(`${getBaseUrl()}/superadminboard/admins`);
            setAdmins(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            showMessage(error.response?.data?.message || "Failed to fetch admins");
        }
    };

    useEffect(() => {
        if (superAdmin) {
            fetchAdmins();
        }
    }, [superAdmin]);

    const handleSuperAdminLogin = async (event) => {
        event.preventDefault();
        if (!login.email || !login.password) {
            showMessage("Email and password are required");
            return;
        }

        setLoading(true);
        try {
            const result = await axios.post(`${getBaseUrl()}/superadminboard`, login, {
                headers: { "Content-Type": "application/json" }
            });

            if (result.data?.role === "SuperAdmin") {
                setSuperAdmin(result.data);
            } else {
                showMessage(result.data?.message || "Login failed");
            }
        } catch (error) {
            showMessage(error.response?.data?.message || "Superadmin login failed");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrUpdate = async (event) => {
        event.preventDefault();

        if (!form.name || !form.email || !form.schoolName || (!editingId && !form.password)) {
            showMessage("Name, email, schoolName and password are required for new admin");
            return;
        }

        setLoading(true);
        try {
            if (editingId) {
                const payload = {
                    name: form.name,
                    email: form.email,
                    schoolName: form.schoolName,
                    photo: form.photo
                };
                if (form.password) payload.password = form.password;

                const result = await axios.put(
                    `${getBaseUrl()}/superadminboard/admins/${editingId}`,
                    payload,
                    { headers: { "Content-Type": "application/json" } }
                );

                if (result.data?.message) {
                    showMessage(result.data.message);
                } else {
                    showMessage("Admin updated");
                    setForm(initialForm);
                    setEditingId(null);
                    fetchAdmins();
                }
            } else {
                const result = await axios.post(`${getBaseUrl()}/superadminboard/admins`, form, {
                    headers: { "Content-Type": "application/json" }
                });
                if (result.data?.message) {
                    showMessage(result.data.message);
                } else {
                    showMessage("Admin created");
                    setForm(initialForm);
                    fetchAdmins();
                }
            }
        } catch (error) {
            showMessage(error.response?.data?.message || "Operation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (admin) => {
        setEditingId(admin._id);
        setForm({
            name: admin.name || "",
            email: admin.email || "",
            password: "",
            schoolName: admin.schoolName || "",
            photo: admin.photo || ""
        });
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const result = await axios.delete(`${getBaseUrl()}/superadminboard/admins/${id}`);
            if (result.data?.message && result.data.message !== "No admin found") {
                showMessage(result.data.message);
            } else {
                showMessage("Admin deleted");
                if (editingId === id) {
                    setEditingId(null);
                    setForm(initialForm);
                }
                fetchAdmins();
            }
        } catch (error) {
            showMessage(error.response?.data?.message || "Delete failed");
        } finally {
            setLoading(false);
        }
    };

    // ---------------- Finance Code Management ----------------

    const fetchFinanceCodeStatuses = async () => {
        const statuses = {};
        for (const admin of admins) {
            try {
                const result = await axios.get(`${getBaseUrl()}/FinanceCode/Status/${admin._id}`);
                statuses[admin._id] = result.data?.hasCode || false;
            } catch {
                statuses[admin._id] = false;
            }
        }
        setFinanceCodes(statuses);
    };

    useEffect(() => {
        if (admins.length > 0) {
            fetchFinanceCodeStatuses();
        }
    }, [admins]);

    const handleFinanceCodeInput = (adminId, value) => {
        // Only allow digits, max 6
        const cleaned = value.replace(/\D/g, "").slice(0, 6);
        setFinanceCodeInputs((prev) => ({ ...prev, [adminId]: cleaned }));
    };

    const handleSetFinanceCode = async (schoolId) => {
        const code = financeCodeInputs[schoolId] || "";
        if (code.length !== 6) {
            showMessage("Finance code must be exactly 6 digits");
            return;
        }

        setFinanceCodeLoading((prev) => ({ ...prev, [schoolId]: true }));
        try {
            const result = await axios.post(
                `${getBaseUrl()}/FinanceCode/${schoolId}`,
                { code },
                { headers: { "Content-Type": "application/json" } }
            );
            showMessage(result.data?.message || "Finance code set successfully");
            setFinanceCodeInputs((prev) => ({ ...prev, [schoolId]: "" }));
            fetchFinanceCodeStatuses();
        } catch (error) {
            showMessage(error.response?.data?.message || "Failed to set finance code");
        } finally {
            setFinanceCodeLoading((prev) => ({ ...prev, [schoolId]: false }));
        }
    };

    const handleDeleteFinanceCode = async (schoolId) => {
        setFinanceCodeLoading((prev) => ({ ...prev, [schoolId]: true }));
        try {
            const result = await axios.delete(`${getBaseUrl()}/FinanceCode/${schoolId}`);
            showMessage(result.data?.message || "Finance code deleted");
            fetchFinanceCodeStatuses();
        } catch (error) {
            showMessage(error.response?.data?.message || "Failed to delete finance code");
        } finally {
            setFinanceCodeLoading((prev) => ({ ...prev, [schoolId]: false }));
        }
    };

    if (!superAdmin) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    background: "linear-gradient(135deg, #1f1c2c 0%, #928dab 100%)"
                }}
            >
                <Paper
                    elevation={12}
                    sx={{
                        width: "100%",
                        maxWidth: 450,
                        borderRadius: 3,
                        p: 4,
                        background: "rgba(255, 255, 255, 0.94)"
                    }}
                >
                    <Stack alignItems="center" spacing={1}>
                        <Avatar sx={{ bgcolor: "#512da8", width: 56, height: 56 }}>SA</Avatar>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>SuperAdmin Board</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
                            Login with superadmin credentials to manage all admins.
                        </Typography>
                    </Stack>
                    <Box component="form" onSubmit={handleSuperAdminLogin}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Email"
                            value={login.email}
                            onChange={(e) => setLogin((prev) => ({ ...prev, email: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            type="password"
                            label="Password"
                            value={login.password}
                            onChange={(e) => setLogin((prev) => ({ ...prev, password: e.target.value }))}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{ mt: 2, py: 1.2, fontWeight: 600, borderRadius: 2 }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : "Login"}
                        </Button>
                    </Box>
                </Paper>
                <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                px: 2,
                py: 4,
                background: "linear-gradient(135deg, #f6f8ff 0%, #eef2ff 100%)"
            }}
        >
            <Box sx={{ maxWidth: 1100, mx: "auto" }}>
                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 2,
                        mb: 3,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>SuperAdmin Board</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Logged in as {superAdmin.email}
                        </Typography>
                    </Box>
                    <Button color="error" variant="outlined" onClick={handleLogout}>
                        Logout
                    </Button>
                </Paper>

                <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <Card sx={{ borderRadius: 3, boxShadow: "0 10px 30px rgba(79, 70, 229, 0.1)" }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>{boardTitle}</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box component="form" onSubmit={handleCreateOrUpdate}>
                                <TextField fullWidth margin="normal" label="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                                <TextField fullWidth margin="normal" label="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                                <TextField fullWidth margin="normal" label="School Name" value={form.schoolName} onChange={(e) => setForm((p) => ({ ...p, schoolName: e.target.value }))} />
                                <TextField fullWidth margin="normal" label="Photo URL (optional)" value={form.photo} onChange={(e) => setForm((p) => ({ ...p, photo: e.target.value }))} />
                                <TextField fullWidth margin="normal" type="password" label={editingId ? "New Password (optional)" : "Password"} value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
                                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                    <Button type="submit" variant="contained">
                                        {loading ? <CircularProgress size={20} color="inherit" /> : editingId ? "Update" : "Create"}
                                    </Button>
                                    {editingId && (
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                setEditingId(null);
                                                setForm(initialForm);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Card sx={{ borderRadius: 3, boxShadow: "0 10px 30px rgba(79, 70, 229, 0.1)" }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Existing Admins ({admins.length})
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={1.5}>
                                {admins.map((admin) => (
                                    <Box
                                        key={admin._id}
                                        sx={{
                                            border: "1px solid #dbe3ff",
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            background: "#fafbff"
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar src={admin.photo || ""}>
                                                {admin.name?.slice(0, 1)?.toUpperCase() || "A"}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600 }}>{admin.name}</Typography>
                                                <Typography variant="body2">{admin.email}</Typography>
                                                <Typography variant="body2" color="text.secondary">{admin.schoolName}</Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="outlined" onClick={() => handleEdit(admin)}>Edit</Button>
                                            <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(admin._id)}>Delete</Button>
                                        </Stack>
                                    </Box>
                                ))}
                                {admins.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No admins found.
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                </Grid>

                {/* Finance Code Management Section */}
                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 2,
                        mt: 3
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <LockIcon sx={{ color: "#512da8" }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Finance Access Codes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            (6-digit codes protect the FINANCE section per school)
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                        {admins.map((admin) => {
                            const hasCode = financeCodes[admin._id];
                            const codeInput = financeCodeInputs[admin._id] || "";
                            const isCodeLoading = financeCodeLoading[admin._id] || false;

                            return (
                                <Box
                                    key={`fc-${admin._id}`}
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 2,
                                        p: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 1
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {admin.schoolName || admin.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {admin.email}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Chip
                                            label={hasCode ? "Code Set" : "No Code"}
                                            color={hasCode ? "success" : "default"}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                        <TextField
                                            size="small"
                                            placeholder="6-digit code"
                                            value={codeInput}
                                            onChange={(e) => handleFinanceCodeInput(admin._id, e.target.value)}
                                            inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
                                            sx={{ width: 130 }}
                                            disabled={isCodeLoading}
                                        />
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleSetFinanceCode(admin._id)}
                                            disabled={isCodeLoading || codeInput.length !== 6}
                                            sx={{ textTransform: "none", fontWeight: 600, minWidth: 56 }}
                                        >
                                            {isCodeLoading ? <CircularProgress size={16} color="inherit" /> : hasCode ? "Update" : "Set"}
                                        </Button>
                                        {hasCode && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteFinanceCode(admin._id)}
                                                disabled={isCodeLoading}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Box>
                            );
                        })}
                        {admins.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No schools configured yet. Create an admin first.
                            </Typography>
                        )}
                    </Stack>
                </Paper>
            </Box>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default SuperAdminBoardPage;
